import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, Lock, Users } from "lucide-react";
import { LOGO_URL, subscribeEmail, getSubscriberCount, LAUNCH_DATE } from "@/lib/api";
import { Countdown } from "@/components/Countdown";
import { InstallAppButton } from "@/components/InstallAppButton";
import { IntroSplash } from "@/components/IntroSplash";
import { MusicPlayer } from "@/components/MusicPlayer";
import { SocialLinks } from "@/components/SocialLinks";
import { toast } from "sonner";

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(null);

  const loadCount = () => getSubscriberCount().then((d) => setWaiting(d.count)).catch(() => {});
  useEffect(() => { loadCount(); }, []);

  // Auto-unlock the vault the instant the countdown hits zero — reload so the full site reveals, no manual step.
  useEffect(() => {
    const launch = new Date(LAUNCH_DATE).getTime();
    const t = setInterval(() => {
      if (Date.now() >= launch) window.location.reload();
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const notify = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Enter your email");
    setBusy(true);
    try {
      await subscribeEmail(email);
      setDone(true);
      setEmail("");
      loadCount();
      toast.success("You're on the list! We'll ping you at launch. 🍭");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-candy-bg relative overflow-hidden">
      <IntroSplash />

      <div className="absolute inset-0 holo-bg opacity-10" />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pt-14 pb-8 text-center">
        <motion.img
          initial={{ scale: 0.6, opacity: 0, rotate: -10 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ duration: 0.7, type: "spring" }}
          src={LOGO_URL}
          alt="The Sweet Tooth Bandit"
          className="mx-auto w-40 sm:w-52 rounded-full animate-float drop-shadow-[8px_8px_0_rgba(0,0,0,0.3)]"
        />
        <div className="mt-6 inline-flex items-center gap-2 bg-black text-white rounded-full px-4 py-1.5 font-mono text-xs font-bold uppercase tracking-widest">
          <Lock className="h-4 w-4" /> Vault opens July 16, 2026 · 11:59 PM
        </div>
        <h1 className="mt-5 font-display text-6xl sm:text-8xl leading-[0.85] tracking-tight">
          THE SWEET <span className="text-candy-pink drop-shadow-[4px_4px_0_#000]">TOOTH</span>{" "}
          <span className="text-candy-teal drop-shadow-[4px_4px_0_#000]">BANDIT</span>
        </h1>
        <p className="mt-5 font-body font-medium text-candy-ink/80 max-w-md mx-auto">
          Something sweet is coming. Nostalgic Mars &amp; Hershey candy, custom candy bouquets,
          and bandit merch. The vault stays locked until launch — get on the list and be first in.
        </p>
        {waiting !== null && waiting > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-6 inline-flex items-center gap-2 bg-candy-teal border-4 border-black rounded-full px-5 py-2 shadow-brutal-sm"
            data-testid="waiting-counter"
          >
            <Users className="h-5 w-5" />
            <span className="font-heading font-700 text-sm sm:text-base">
              {waiting.toLocaleString()} {waiting === 1 ? "bandit" : "bandits"} already waiting in line
            </span>
          </motion.div>
        )}
      </div>

      <Countdown />

      <div className="relative max-w-md mx-auto px-4 sm:px-6 py-12 text-center">
        {done ? (
          <div className="bg-candy-teal border-4 border-black rounded-2xl p-6 shadow-brutal">
            <h3 className="font-display text-3xl">You're in! 🎉</h3>
            <p className="text-sm font-medium mt-1">We'll email you the second the vault opens.</p>
          </div>
        ) : (
          <form onSubmit={notify} className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal" data-testid="notify-form">
            <h3 className="font-heading font-700 text-xl flex items-center justify-center gap-2">
              <Mail className="h-5 w-5" /> Get notified at launch
            </h3>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <input
                data-testid="notify-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-1 bg-candy-bg border-4 border-black rounded-lg px-3 py-2.5 font-medium focus:outline-none focus:shadow-brutal-teal transition-shadow"
              />
              <button
                data-testid="notify-submit"
                type="submit"
                disabled={busy}
                className="bg-candy-pink text-white font-bold uppercase tracking-wider py-2.5 px-5 border-4 border-black rounded-lg shadow-brutal-sm active:translate-x-1 active:translate-y-1 active:shadow-none transition-transform disabled:opacity-60"
              >
                {busy ? "…" : "Notify Me"}
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 flex flex-col items-center gap-4">
          <InstallAppButton />
          <p className="font-mono text-[11px] text-candy-ink/60 uppercase tracking-widest">
            Install the app so launch day is one tap away
          </p>
          <div className="flex gap-3 items-center">
            <SocialLinks tileClass="bg-white border-4 border-black rounded-xl p-2.5 shadow-brutal-sm hover:bg-candy-pink hover:text-white transition-colors" iconClass="h-5 w-5" />
            <a href="mailto:sweettoothbanditshop@outlook.com" aria-label="Email" className="bg-white border-4 border-black rounded-xl p-2.5 shadow-brutal-sm"><Mail className="h-5 w-5" /></a>
          </div>
        </div>
      </div>

      <MusicPlayer />

      <footer className="relative border-t-4 border-black py-6 text-center text-xs font-mono text-candy-ink/50">
        © {new Date().getFullYear()} The Sweet Tooth Bandit Candy Co. · Est. 2023
        <a href="/admin" data-testid="comingsoon-admin-link" className="block mt-2 text-candy-ink/40 hover:text-candy-pink uppercase tracking-widest">
          Owner Login
        </a>
      </footer>
    </div>
  );
}
